// src/components/admin/SupportTickets.jsx
import React, { useState, useEffect, useMemo } from 'react';
import './SupportTickets.css';

const REQUEST_TYPE_LABELS = {
  add_more_products: 'Add more products',
  change_banner: 'Change banner',
  add_new_category: 'Add new category',
  store_category_change: 'Store category change',
  payment_issue: 'Payment issue',
  domain_issue: 'Domain issue',
  store_not_working: 'Store not working',
  plan_upgrade: 'Plan upgrade',
  other_request: 'Other request',
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_for_customer', label: 'Waiting for Customer' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_CLASSES = {
  open: 'status-open',
  in_progress: 'status-in-progress',
  waiting_for_customer: 'status-waiting',
  completed: 'status-completed',
};

const PRIORITY_CLASSES = {
  low: 'priority-low',
  medium: 'priority-medium',
  high: 'priority-high',
};

const SupportTickets = () => {
  const [stats, setStats] = useState({ open: 0, in_progress: 0, waiting_for_customer: 0, completed: 0 });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const [selectedId, setSelectedId] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketUpdates, setTicketUpdates] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await fetch('https://creativepulse.pk/main-api/get-support-tickets.php');
      const data = await res.json();
      if (data.success) {
        setTickets(data.tickets || []);
        setStats(data.stats || stats);
      }
    } catch (e) {
      console.error('Error fetching tickets:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const requestTypeLabel = (v) => REQUEST_TYPE_LABELS[v] || v;

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch = !term ||
        (t.ticket_number || '').toLowerCase().includes(term) ||
        (t.subject || '').toLowerCase().includes(term) ||
        (t.store_admin_email || '').toLowerCase().includes(term);
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || t.priority === priorityFilter;
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [tickets, searchTerm, statusFilter, priorityFilter]);

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
  };

  const openTicket = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    setTicketDetail(null);
    setTicketUpdates([]);
    try {
      const res = await fetch(`https://creativepulse.pk/main-api/get-support-ticket-detail.php?id=${id}`);
      const data = await res.json();
      if (data.success) {
        setTicketDetail(data.ticket);
        setTicketUpdates(data.updates || []);
      }
    } catch (e) {
      console.error('Error fetching ticket detail:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeTicket = () => {
    setSelectedId(null);
    setTicketDetail(null);
    setTicketUpdates([]);
    setReplyMessage('');
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) return;
    setReplySubmitting(true);
    try {
      const res = await fetch('https://creativepulse.pk/main-api/reply-support-ticket.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_id: selectedId, message: replyMessage.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setReplyMessage('');
        openTicket(selectedId);
        fetchTickets();
      } else {
        alert(data.message || 'Failed to send reply');
      }
    } catch (e) {
      alert('Connection error');
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusSaving(true);
    try {
      const res = await fetch('https://creativepulse.pk/main-api/update-support-ticket-status.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedId, status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        setTicketDetail(prev => ({ ...prev, status: newStatus }));
        fetchTickets();
      } else {
        alert(data.message || 'Failed to update status');
      }
    } catch (e) {
      alert('Connection error');
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <div className="support-tickets-page">
      <div className="st-header">
        <div>
          <h1>Support Tickets</h1>
          {/* TODO: currently only reads from the demo-store's database.
              Once more stores exist, this needs to aggregate across all
              of them instead of a single hardcoded connection. */}
          <p>Requests submitted by store owners across all connected stores.</p>
        </div>
      </div>

      <div className="st-stats-grid">
        <div className="st-stat-card">
          <p className="st-stat-title">Open</p>
          <h3 className="st-stat-value">{loading ? '—' : stats.open}</h3>
        </div>
        <div className="st-stat-card">
          <p className="st-stat-title">In Progress</p>
          <h3 className="st-stat-value">{loading ? '—' : stats.in_progress}</h3>
        </div>
        <div className="st-stat-card">
          <p className="st-stat-title">Waiting for Customer</p>
          <h3 className="st-stat-value">{loading ? '—' : stats.waiting_for_customer}</h3>
        </div>
        <div className="st-stat-card">
          <p className="st-stat-title">Completed</p>
          <h3 className="st-stat-value">{loading ? '—' : stats.completed}</h3>
        </div>
      </div>

      <div className="st-filter-bar">
        <input
          type="text"
          className="st-search"
          placeholder="Search ticket ID, subject, or store email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
          <option value="all">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <button className="st-reset-btn" onClick={resetFilters}>Reset</button>
      </div>

      {loading ? (
        <div className="st-loading">Loading tickets...</div>
      ) : filteredTickets.length === 0 ? (
        <div className="st-empty">No support tickets found.</div>
      ) : (
        <div className="st-table-container">
          <table className="st-table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Store</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.map(t => (
                <tr key={t.id}>
                  <td className="st-ticket-id">{t.ticket_number}</td>
                  <td className="st-muted">{t.store_admin_email || '—'}</td>
                  <td>{t.subject}</td>
                  <td className="st-muted">{requestTypeLabel(t.request_type)}</td>
                  <td><span className={`st-priority-pill ${PRIORITY_CLASSES[t.priority] || ''}`}>{t.priority}</span></td>
                  <td><span className={`st-status-pill ${STATUS_CLASSES[t.status] || ''}`}>{STATUS_OPTIONS.find(s => s.value === t.status)?.label || t.status}</span></td>
                  <td className="st-muted">{formatDate(t.created_at)}</td>
                  <td><button className="st-view-btn" onClick={() => openTicket(t.id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <div className="st-drawer-overlay">
          <div className="st-drawer-backdrop" onClick={closeTicket}></div>
          <div className="st-drawer">
            {detailLoading || !ticketDetail ? (
              <div className="st-drawer-loading">Loading...</div>
            ) : (
              <>
                <div className="st-drawer-header">
                  <div>
                    <h2>{ticketDetail.ticket_number}</h2>
                    <p className="st-drawer-store">{ticketDetail.store_admin_email}</p>
                  </div>
                  <button className="st-drawer-close" onClick={closeTicket}>×</button>
                </div>

                <div className="st-drawer-body">
                  <div className="st-info-card">
                    <h3>{ticketDetail.subject}</h3>
                    <div className="st-info-grid">
                      <div><span className="st-info-label">Type</span><span>{requestTypeLabel(ticketDetail.request_type)}</span></div>
                      <div><span className="st-info-label">Priority</span><span>{ticketDetail.priority}</span></div>
                      <div><span className="st-info-label">Created</span><span>{formatDate(ticketDetail.created_at)}</span></div>
                      <div><span className="st-info-label">Contact</span><span>{ticketDetail.contact_detail || '—'}</span></div>
                    </div>
                    <div className="st-description">
                      <span className="st-info-label">Description</span>
                      <p>{ticketDetail.description}</p>
                    </div>
                    {ticketDetail.attachment && (
                      <div className="st-attachment">
                        <a href={ticketDetail.attachment} target="_blank" rel="noopener noreferrer">
                          <img src={ticketDetail.attachment} alt="Attachment" />
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="st-status-changer">
                    <label>Update Status</label>
                    <select
                      value={ticketDetail.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={statusSaving}
                    >
                      {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>

                  <div className="st-conversation">
                    <h3>Conversation</h3>
                    <div className="st-thread">
                      {ticketUpdates.map(u => (
                        <div key={u.id} className={`st-msg ${u.author_type === 'admin_team' ? 'st-msg-team' : 'st-msg-owner'}`}>
                          <div className="st-msg-header">
                            <span>{u.author_type === 'admin_team' ? 'Team (You)' : 'Store Owner'}</span>
                            <span className="st-msg-date">{formatDate(u.created_at)}</span>
                          </div>
                          <p>{u.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="st-reply-box">
                    <label>Reply as Team</label>
                    <textarea
                      rows="3"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply to the store owner..."
                    />
                    <button className="st-send-btn" onClick={handleReply} disabled={replySubmitting || !replyMessage.trim()}>
                      {replySubmitting ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;